import React from "react";
import {
  Card,
  Badge,
  OverlayTrigger,
  Tooltip,
  ProgressBar,
} from "react-bootstrap";
import useDeviceType from "../../hooks/useDeviceType";

/**
 * ActiveMQHealth
 *
 * Props:
 *   activemq: {
 *     status: "UP" | "DOWN" | "WARN" | string,
 *     state: "RUNNING_ACTIVE" | "RUNNING_IDLE" | "RUNNING_BACKLOG" | "PAUSED" | "BROKER_DOWN" | string,
 *     listener?: { state: "RUNNING" | "PAUSED" | "MISSING" | string, id?: string },
 *     lastMessage?: { receivedTotal?: number, athensTime?: string, secondsSince?: number },
 *     queueDepthSample?: number
 *   }
 *
 * UI/UX notes:
 * - Top status pill reflects broker connectivity (UP/DOWN).
 * - “State” chip summarizes flow status (active/idle/backlog/paused).
 * - Listener tile shows running/paused and id.
 * - Last message tile shows relative age + total received.
 * - Queue depth tile shows backlog with contextual color.
 */
const ActiveMQHealth = ({ activemq }) => {
  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";
  const isTablet = deviceType === "tablet";
  const isDesktop = deviceType === "desktop";
  const {
    status = "UNKNOWN",
    state = "UNKNOWN",
    listener = {},
    lastMessage = {},
    queueDepthSample,
  } = activemq || {};

  const statusVariant =
    {
      UP: "success",
      DOWN: "danger",
      WARN: "warning",
      UNKNOWN: "secondary",
    }[status] || "secondary";

  const stateVariant =
    {
      RUNNING_ACTIVE: "success",
      RUNNING_IDLE: "info",
      RUNNING_BACKLOG: "warning",
      PAUSED: "secondary",
      BROKER_DOWN: "danger",
      UNKNOWN: "secondary",
    }[state] || "secondary";

  const listenerVariant =
    {
      RUNNING: "success",
      PAUSED: "secondary",
      MISSING: "warning",
    }[listener.state] || "secondary";

  const depth = typeof queueDepthSample === "number" ? queueDepthSample : null;
  const depthVariant =
    depth === null
      ? "secondary"
      : depth === 0
      ? "success"
      : depth < 50
      ? "warning"
      : "danger";

  // Seconds since last message → small progress/heat indicator
  const secondsSince =
    typeof lastMessage.secondsSince === "number"
      ? lastMessage.secondsSince
      : null;
  // Cap the bar at 5 minutes for visualization
  const maxWindowSec = 300;
  const progressNow =
    secondsSince == null
      ? 0
      : Math.min(100, Math.round((secondsSince / maxWindowSec) * 100));
  const progressVariant =
    secondsSince == null
      ? "secondary"
      : secondsSince <= 60
      ? "success"
      : secondsSince <= 180
      ? "warning"
      : "danger";

  const Label = ({ title, children }) => (
    <div className="d-flex flex-column gap-1">
      <div className="text-muted small">{title}</div>
      <div className="fw-semibold">{children}</div>
    </div>
  );

  const Pill = ({ text, variant, tooltip }) => {
    const pill = (
      <Badge bg={variant} className="rounded-pill px-3 py-2">
        {text}
      </Badge>
    );
    return tooltip ? (
      <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
        <span>{pill}</span>
      </OverlayTrigger>
    ) : (
      pill
    );
  };

  return (
    <Card className="flex-fill w-100">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <Card.Title className="mb-0">ActiveMQ</Card.Title>
        <div className="d-flex gap-2 align-items-center">
          <Pill
            text={`Broker: ${status}`}
            variant={statusVariant}
            tooltip="Connectivity to the ActiveMQ broker"
          />
          <Pill
            text={state.replace(/_/g, " ")}
            variant={stateVariant}
            tooltip="Overall consumer/flow state"
          />
        </div>
      </Card.Header>

      <Card.Body>
        <div className="row g-3">
          {/* Listener */}
          <div className="col-12 col-md-4">
            <div className="border rounded p-3 h-100">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">Listener</div>
                <Badge bg={listenerVariant}>
                  {listener.state || "UNKNOWN"}
                </Badge>
              </div>
              <Label title="ID">{listener.id || "-"}</Label>
            </div>
          </div>

          {/* Last message */}
          <div className="col-12 col-md-5">
            <div className="border rounded p-3 h-100">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">Last Message</div>
                <Badge bg={progressVariant}>
                  {secondsSince == null ? "never" : `${secondsSince}s ago`}
                </Badge>
              </div>

              <div className="d-flex flex-wrap gap-4 mb-2">
                <Label title="Received total">
                  {lastMessage.receivedTotal != null
                    ? lastMessage.receivedTotal
                    : "—"}
                </Label>
                <Label title="Athens time">
                  {lastMessage.athensTime || "—"}
                </Label>
              </div>

              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Relative silence window visualized over 5 minutes. Lower is
                    better.
                  </Tooltip>
                }
              >
                <div>
                  <ProgressBar now={progressNow} variant={progressVariant} />
                </div>
              </OverlayTrigger>
            </div>
          </div>

          {/* Queue depth */}
          <div className="col-12 col-md-3">
            <div className="border rounded p-3 h-100">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">Queue Depth (sample)</div>
                <Badge bg={depthVariant}>
                  {depth === null ? "n/a" : depth}
                </Badge>
              </div>
              <div className="text-muted small">
                {depth === null
                  ? "Depth not available"
                  : depth === 0
                  ? "No backlog"
                  : depth < 50
                  ? "Small backlog"
                  : "Large backlog"}
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        {isDesktop && (
          <div className="mt-3 text-muted small">
            <span className="me-3">
              <Badge bg="success">Active</Badge> healthy
            </span>
            <span className="me-3">
              <Badge bg="warning">Warning</Badge> attention needed
            </span>
            <span className="me-3">
              <Badge bg="danger">Down</Badge> action required
            </span>
            <span className="me-3">
              <Badge bg="secondary">Paused/Unknown</Badge>
            </span>
          </div>
        )}
        {(isTablet || isMobile) && (
          <div className="mt-3 text-muted small">
            <div className="mb-3">
              <Badge bg="success">Active</Badge> healthy
            </div>
            <div className="mb-3">
              <Badge bg="warning">Warning</Badge> attention needed
            </div>
            <div className="mb-3">
              <Badge bg="danger">Down</Badge> action required
            </div>
            <div className="mb-3">
              <Badge bg="secondary">Paused/Unknown</Badge>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ActiveMQHealth;
